<template>
  <div
    class="theme-wrap"
    :class="isDark ? 'is-dark' : 'is-light'"
    @keydown.esc="showDropdown = false"
    ref="wrap"
  >
    <!-- Trigger -->
    <button
      type="button"
      class="theme-btn"
      :class="{ disabled: isDisabled }"
      :aria-expanded="showDropdown ? 'true' : 'false'"
      :title="$t('theme.select')"
      @click.stop="toggle"
    >
      <span class="icon" v-html="currentTheme.icon"></span>
      <span class="label">{{ currentTheme.name }}</span>
      <span class="chev" aria-hidden="true">▾</span>
    </button>

    <!-- Dropdown -->
    <transition name="fade-scale">
      <div
        v-show="showDropdown"
        class="menu"
        role="menu"
        @click.stop
      >
        <div
          v-for="theme in themes"
          :key="theme.value"
          class="item"
          :class="{ active: theme.value === isDark }"
          role="menuitem"
          tabindex="0"
          @click="selectTheme(theme)"
          @keydown.enter.prevent="selectTheme(theme)"
        >
          <span class="icon" v-html="theme.icon"></span>
          <span class="text">{{ theme.name }}</span>
          <span class="tick" v-if="theme.value === isDark">✔</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ThemeToggle',
  data() {
    return { showDropdown: false }
  },
  computed: {
    isDark() {
      return this.$store.state.ui.dark
    },
    isDisabled() {
      return this.$store.state.ui.modeDisabled
    },
    themes() {
      return [
        {
          value: false,
          name: this.$t('theme.light'),
          icon: '☀️'
        },
        {
          value: true,
          name: this.$t('theme.dark'),
          icon: '🌙'
        }
      ]
    },
    currentTheme() {
      return this.themes.find(t => t.value === this.isDark) || this.themes[0]
    }
  },
  mounted() {
    document.addEventListener('click', this.onOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onOutside)
  },
  methods: {
    toggle() {
      if (this.isDisabled) return
      this.showDropdown = !this.showDropdown
    },
    selectTheme(theme) {
      this.showDropdown = false
      if (theme.value !== this.isDark) {
        this.$store.dispatch('TOGGLE_DARK')
      }
    },
    onOutside(e) {
      if (!this.$refs.wrap) return
      if (!this.$refs.wrap.contains(e.target)) this.showDropdown = false
    }
  }
}
</script>

<style scoped>
/* --- Container --- */
.theme-wrap {
  position: relative;
  display: inline-block;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
}

/* Preset biến theo theme */
.theme-wrap.is-dark {
  --bg: #1f2430;
  --bg-hover: #252b3a;
  --bd: #2b3040;
  --bd-hover: #3a4156;
  --text: #ffffff;

  --menu-bg: #1f2430;
  --menu-bd: #2b3040;
  --menu-shadow: 0 10px 30px rgba(0,0,0,.35);
  --active-bg: #2a3142;

  --focus: #3a7afe55;
}

.theme-wrap.is-light {
  --bg: #f3f4f6;
  --bg-hover: #e9edf3;
  --bd: #d5d9e0;
  --bd-hover: #c8cdd6;
  --text: #111827;

  --menu-bg: #ffffff;
  --menu-bd: #e5e7eb;
  --menu-shadow: 0 12px 24px rgba(17,24,39,.10);
  --active-bg: #eef2f7;

  --focus: #3a7afe55;
}

/* --- Trigger button (pill) --- */
.theme-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  height: 32px;
  background: var(--bg);
  color: var(--text);
  border: 1px solid var(--bd);
  border-radius: 10px;
  cursor: pointer;
  line-height: 1;
  transition: background .15s, border-color .15s, transform .02s, box-shadow .15s;
}
.theme-btn:hover { background: var(--bg-hover); border-color: var(--bd-hover); }
.theme-btn:active { transform: translateY(1px); }
.theme-btn:focus { outline: 2px solid var(--focus); outline-offset: 2px; }
.theme-btn.disabled { opacity: .5; cursor: not-allowed; }

/* icon + text + caret */
.icon { 
  font-size: 14px; 
  display: inline-flex; 
  align-items: center;
  justify-content: center;
  line-height: 1;
  height: 16px;
  width: 16px;
}
.label {
  font-size: 14px;
  white-space: nowrap;
  font-weight: 600;
  line-height: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  margin-bottom: 0 !important;
  .is-dark & {
    color: #ffffff;
  }
}
.chev { 
  font-size: 12px; 
  opacity: .7; 
  margin-left: 2px; 
  line-height: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
}

/* --- Dropdown menu --- */
.menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 140px;
  background: var(--menu-bg);
  border: 1px solid var(--menu-bd);
  border-radius: 10px;
  box-shadow: var(--menu-shadow);
  padding: 6px;
  z-index: 9999;
}

/* Item row */
.item {
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--text);
  cursor: pointer;
  transition: background .12s, color .12s, border-color .12s;
}
.item:hover { background: var(--active-bg); }
.item.active {
  background: var(--active-bg);
  border: 1px solid var(--bd-hover);
}
.item .text { font-size: 14px; }

/* check mark */
.tick { font-size: 12px; opacity: .9; }

/* --- Transition --- */
.fade-scale-enter-active,
.fade-scale-leave-active { transition: opacity .12s ease, transform .12s ease; }
.fade-scale-enter, .fade-scale-leave-to { opacity: 0; transform: scale(.98); }
</style>
